import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface VideoItem {
  id: string;
  filename: string;
  size: number;
  uploadDate: string;
  url: string;
  tags: string[];
  description: string;
  title?: string;
  group: string;
  viewCount?: number;
  likeCount?: number;
  watchTimeStats?: {
    totalWatchTime: number;
    averageWatchTime: number;
    completionRate: number;
    totalViews: number;
    completedViews: number;
  };
}

const AnalyticsPage: React.FC = () => {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 获取所有视频数据
  const fetchAllVideos = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/videos');
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
        setError(null);
        
        // 缓存数据到 localStorage
        localStorage.setItem('analyticsData', JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } else {
        // 尝试从缓存加载
        const cached = localStorage.getItem('analyticsData');
        if (cached) {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          // 如果缓存不超过1小时，使用缓存
          if (Date.now() - timestamp < 3600000) {
            setVideos(cachedData);
            setError('网络错误，显示缓存数据');
          } else {
            setError('获取视频数据失败');
          }
        } else {
          setError('获取视频数据失败');
        }
      }
    } catch (err) {
      // 网络错误时尝试使用缓存
      const cached = localStorage.getItem('analyticsData');
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 3600000) {
          setVideos(cachedData);
          setError('网络错误，显示缓存数据');
        } else {
          setError('网络错误，请检查服务器是否运行');
        }
      } else {
        setError('网络错误，请检查服务器是否运行');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllVideos();
  }, []);

  // 准备图表数据
  const getWatchTimeData = () => {
    return videos
      .filter(video => video.watchTimeStats)
      .map(video => ({
        name: video.title || video.filename,
        averageWatchTime: Math.round((video.watchTimeStats?.averageWatchTime || 0) / 1000),
        completionRate: video.watchTimeStats?.completionRate || 0,
        totalViews: video.watchTimeStats?.totalViews || 0
      }))
      .sort((a, b) => b.totalViews - a.totalViews)
      .slice(0, 10); // 只显示前10个
  };

  const getCompletionRateData = () => {
    return videos
      .filter(video => video.watchTimeStats)
      .map(video => ({
        name: video.title || video.filename,
        completionRate: video.watchTimeStats?.completionRate || 0
      }))
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 10);
  };

  const getGroupStats = () => {
    const groupMap = new Map<string, { totalViews: number; totalWatchTime: number; videoCount: number }>();
    
    videos.forEach(video => {
      if (video.watchTimeStats) {
        const group = video.group || '默认分组';
        if (!groupMap.has(group)) {
          groupMap.set(group, { totalViews: 0, totalWatchTime: 0, videoCount: 0 });
        }
        const stats = groupMap.get(group)!;
        stats.totalViews += video.watchTimeStats.totalViews;
        stats.totalWatchTime += video.watchTimeStats.totalWatchTime;
        stats.videoCount += 1;
      }
    });

    return Array.from(groupMap.entries()).map(([name, stats]) => ({
      name,
      value: stats.totalViews,
      averageWatchTime: Math.round(stats.totalWatchTime / stats.videoCount / 1000)
    }));
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-white text-lg">加载统计数据中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-red-400 text-center p-4">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2">加载失败</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={fetchAllVideos}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  const hasStats = videos.some(video => video.watchTimeStats);
  if (!hasStats) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-white text-center p-4 max-w-md">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-xl font-semibold mb-2">暂无统计数据</h2>
          <p className="text-gray-400 mb-6">
            请先观看一些视频，系统会自动收集用户停留时间和完播率数据。
          </p>
          <a 
            href="/" 
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-lg font-semibold transition-colors block text-center"
          >
            🎥 去观看视频
          </a>
        </div>
      </div>
    );
  }

  const watchTimeData = getWatchTimeData();
  const completionRateData = getCompletionRateData();
  const groupStats = getGroupStats();

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">📊 视频统计分析</h1>
          <p className="text-gray-400">用户停留时间与完播率数据分析</p>
        </div>

        {/* 总体概览 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-blue-400">
              {videos.filter(v => v.watchTimeStats).length}
            </div>
            <div className="text-gray-400">有统计数据的视频</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-green-400">
              {videos.reduce((sum, v) => sum + (v.watchTimeStats?.totalViews || 0), 0)}
            </div>
            <div className="text-gray-400">总观看次数</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-purple-400">
              {Math.round(videos.reduce((sum, v) => sum + (v.watchTimeStats?.averageWatchTime || 0), 0) / videos.filter(v => v.watchTimeStats).length / 1000) || 0}s
            </div>
            <div className="text-gray-400">平均观看时长</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-yellow-400">
              {Math.round(videos.reduce((sum, v) => sum + (v.watchTimeStats?.completionRate || 0), 0) / videos.filter(v => v.watchTimeStats).length) || 0}%
            </div>
            <div className="text-gray-400">平均完播率</div>
          </div>
        </div>

        {/* 图表区域 */}
        <div className="space-y-8">
          {/* 平均观看时长对比 */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">📈 热门视频平均观看时长（秒）</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={watchTimeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="name" stroke="#aaa" />
                  <YAxis stroke="#aaa" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="averageWatchTime" name="平均观看时长(秒)" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 完播率对比 */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">✅ 视频完播率排名</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={completionRateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="name" stroke="#aaa" />
                  <YAxis stroke="#aaa" domain={[0, 100]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value) => [`${value}%`, '完播率']}
                  />
                  <Legend />
                  <Bar dataKey="completionRate" name="完播率(%)" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 分组统计 */}
          {groupStats.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">📁 按分组统计</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={groupStats}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      >
                        {groupStats.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                        formatter={(value, name, props) => [`${value}次`, '观看次数']}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={groupStats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                      <XAxis dataKey="name" stroke="#aaa" />
                      <YAxis stroke="#aaa" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value) => [`${value}秒`, '平均观看时长']}
                      />
                      <Legend />
                      <Bar dataKey="averageWatchTime" name="平均观看时长(秒)" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* 详细数据表格 */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">📋 详细统计数据</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2">视频标题</th>
                    <th className="text-right py-2">观看次数</th>
                    <th className="text-right py-2">平均时长(秒)</th>
                    <th className="text-right py-2">完播率(%)</th>
                    <th className="text-right py-2">完成次数</th>
                  </tr>
                </thead>
                <tbody>
                  {videos
                    .filter(video => video.watchTimeStats)
                    .map(video => (
                      <tr key={video.id} className="border-b border-gray-700 hover:bg-gray-750">
                        <td className="py-2">{video.title || video.filename}</td>
                        <td className="text-right py-2">{video.watchTimeStats?.totalViews}</td>
                        <td className="text-right py-2">{Math.round((video.watchTimeStats?.averageWatchTime || 0) / 1000)}</td>
                        <td className="text-right py-2">{video.watchTimeStats?.completionRate}</td>
                        <td className="text-right py-2">{video.watchTimeStats?.completedViews}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 返回按钮 */}
        <div className="mt-8 text-center">
          <a 
            href="/admin" 
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            ← 返回管理后台
          </a>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;